import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { bootstrapWorkspace, replaceWorkspace } from '../workspace/workspaceSlice'
import { relayablyDashboardApi } from './relayablyDashboardApi'
import {
  DashboardAgentDetail,
  DashboardAgentUpdatePayload,
  DashboardBootstrapResponse,
  DashboardChatPayload,
  DashboardChatResponse,
  DashboardCallDetail,
  DashboardInstructionPayload,
  DashboardKnowledgeCaptureResponse,
  DashboardKnowledgeFilePayload,
  DashboardKnowledgeItemPayload,
} from './types'

type DashboardState = {
  status: 'idle' | 'loading' | 'error'
  overview: DashboardBootstrapResponse['overview'] | null
  calls: DashboardBootstrapResponse['calls']
  agents: DashboardBootstrapResponse['agents']
  selectedCall: DashboardCallDetail | null
  selectedAgent: DashboardAgentDetail | null
  strategyStatus: 'idle' | 'saving' | 'success' | 'error'
  chatMessage: string | null
  deployStatus: 'idle' | 'saving' | 'success' | 'error'
  errorMessage: string | null
}

const initialState: DashboardState = {
  status: 'idle',
  overview: null,
  calls: [],
  agents: [],
  selectedCall: null,
  selectedAgent: null,
  strategyStatus: 'idle',
  chatMessage: null,
  deployStatus: 'idle',
  errorMessage: null,
}

const requireSessionToken = (state: RootState) => {
  if (!state.auth.sessionToken) {
    throw new Error('You must be signed in to use Relayably.')
  }

  return state.auth.sessionToken
}

export const bootstrapDashboard = createAsyncThunk('dashboard/bootstrap', async (_, thunkApi) => {
  const state = thunkApi.getState() as RootState
  return relayablyDashboardApi.bootstrap(requireSessionToken(state))
})

export const fetchCallDetail = createAsyncThunk('dashboard/call', async (callId: string, thunkApi) => {
  const state = thunkApi.getState() as RootState
  const response = await relayablyDashboardApi.getCall(requireSessionToken(state), callId)
  return response.call
})

export const fetchAgentDetail = createAsyncThunk('dashboard/agent', async (agentId: string, thunkApi) => {
  const state = thunkApi.getState() as RootState
  const response = await relayablyDashboardApi.getAgent(requireSessionToken(state), agentId)
  return response.agent
})

export const saveAgentDetail = createAsyncThunk(
  'dashboard/saveAgent',
  async ({ agentId, payload }: { agentId: string; payload: DashboardAgentUpdatePayload }, thunkApi) => {
    const state = thunkApi.getState() as RootState
    const response = await relayablyDashboardApi.updateAgent(requireSessionToken(state), agentId, payload)
    return response.agent
  }
)

export const deployAgentDetail = createAsyncThunk(
  'dashboard/deployAgent',
  async ({ agentId, payload }: { agentId: string; payload: DashboardAgentUpdatePayload }, thunkApi) => {
    const state = thunkApi.getState() as RootState
    const response = await relayablyDashboardApi.deployAgent(requireSessionToken(state), agentId, payload)
    return response.agent
  }
)

export const saveChat = createAsyncThunk('dashboard/chat', async (payload: DashboardChatPayload, thunkApi) => {
  const state = thunkApi.getState() as RootState
  const response = await relayablyDashboardApi.saveChat(requireSessionToken(state), payload)
  await thunkApi.dispatch(bootstrapWorkspace())
  return response
})

export const saveKnowledgeItem = createAsyncThunk(
  'dashboard/saveKnowledgeItem',
  async (payload: DashboardKnowledgeItemPayload, thunkApi) => {
    const state = thunkApi.getState() as RootState
    const response = await relayablyDashboardApi.saveKnowledgeItem(requireSessionToken(state), payload)
    await thunkApi.dispatch(bootstrapWorkspace())
    return response
  }
)

export const uploadKnowledgeFile = createAsyncThunk(
  'dashboard/uploadKnowledgeFile',
  async (payload: DashboardKnowledgeFilePayload, thunkApi) => {
    const state = thunkApi.getState() as RootState
    const response = await relayablyDashboardApi.uploadKnowledgeFile(requireSessionToken(state), payload)
    await thunkApi.dispatch(bootstrapWorkspace())
    return response
  }
)

export const deleteKnowledgeItem = createAsyncThunk(
  'dashboard/deleteKnowledgeItem',
  async (knowledgeItemId: string, thunkApi) => {
    const state = thunkApi.getState() as RootState
    const currentWorkspace = state.workspace.currentWorkspace
    if (currentWorkspace) {
      thunkApi.dispatch(
        replaceWorkspace({
          ...currentWorkspace,
          workspace: {
            ...currentWorkspace.workspace,
            knowledgeItems: currentWorkspace.workspace.knowledgeItems.filter(item => item.id !== knowledgeItemId),
          },
        })
      )
    }
    try {
      const response = await relayablyDashboardApi.deleteKnowledgeItem(requireSessionToken(state), knowledgeItemId)
      await thunkApi.dispatch(bootstrapWorkspace())
      return response
    } catch (error) {
      await thunkApi.dispatch(bootstrapWorkspace())
      throw error
    }
  }
)

export const saveInstruction = createAsyncThunk(
  'dashboard/saveInstruction',
  async (payload: DashboardInstructionPayload, thunkApi) => {
    const state = thunkApi.getState() as RootState
    const response = await relayablyDashboardApi.saveInstruction(requireSessionToken(state), payload)
    await thunkApi.dispatch(bootstrapWorkspace())
    return response
  }
)

export const deleteInstruction = createAsyncThunk(
  'dashboard/deleteInstruction',
  async (instructionId: string, thunkApi) => {
    const state = thunkApi.getState() as RootState
    const currentWorkspace = state.workspace.currentWorkspace
    if (currentWorkspace) {
      thunkApi.dispatch(
        replaceWorkspace({
          ...currentWorkspace,
          workspace: {
            ...currentWorkspace.workspace,
            instructions: currentWorkspace.workspace.instructions.filter(item => item.id !== instructionId),
          },
        })
      )
    }
    try {
      const response = await relayablyDashboardApi.deleteInstruction(requireSessionToken(state), instructionId)
      await thunkApi.dispatch(bootstrapWorkspace())
      return response
    } catch (error) {
      await thunkApi.dispatch(bootstrapWorkspace())
      throw error
    }
  }
)

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearSelectedCall(state) {
      state.selectedCall = null
    },
    clearSelectedAgent(state) {
      state.selectedAgent = null
    },
    clearDashboardError(state) {
      state.errorMessage = null
    },
  },
  extraReducers: builder => {
    builder
      .addCase(bootstrapDashboard.pending, state => {
        state.status = 'loading'
        state.errorMessage = null
      })
      .addCase(bootstrapDashboard.fulfilled, (state, action: PayloadAction<DashboardBootstrapResponse>) => {
        state.status = 'idle'
        state.overview = action.payload.overview
        state.calls = action.payload.calls
        state.agents = action.payload.agents
      })
      .addCase(bootstrapDashboard.rejected, (state, action) => {
        state.status = 'error'
        state.errorMessage = action.error.message ?? 'Unable to load your dashboard.'
      })
      .addCase(fetchCallDetail.fulfilled, (state, action: PayloadAction<DashboardCallDetail>) => {
        state.selectedCall = action.payload
      })
      .addCase(fetchAgentDetail.fulfilled, (state, action: PayloadAction<DashboardAgentDetail>) => {
        state.selectedAgent = action.payload
      })
      .addCase(saveAgentDetail.fulfilled, (state, action: PayloadAction<DashboardAgentDetail>) => {
        state.selectedAgent = action.payload
        state.agents = state.agents.map(agent =>
          agent.id === action.payload.agent.id
            ? {
                ...agent,
                name: action.payload.agent.name,
                description: action.payload.agent.description,
                voiceLabel: action.payload.agent.voiceLabel,
                status: action.payload.agent.status,
              }
            : agent
        )
      })
      .addCase(deployAgentDetail.pending, state => {
        state.deployStatus = 'saving'
      })
      .addCase(deployAgentDetail.fulfilled, (state, action: PayloadAction<DashboardAgentDetail>) => {
        state.deployStatus = 'success'
        state.selectedAgent = action.payload
        state.agents = state.agents.map(agent =>
          agent.id === action.payload.agent.id
            ? {
                ...agent,
                externalAgentId: action.payload.agent.externalAgentId,
                assignedPhoneNumberId: action.payload.agent.assignedPhoneNumberId,
                assignedPhoneNumber: action.payload.agent.assignedPhoneNumber,
                assignedPhoneNumberLabel: action.payload.agent.assignedPhoneNumberLabel,
                name: action.payload.agent.name,
                description: action.payload.agent.description,
                voiceLabel: action.payload.agent.voiceLabel,
                status: action.payload.agent.status,
              }
            : agent
        )
      })
      .addCase(deployAgentDetail.rejected, (state, action) => {
        state.deployStatus = 'error'
        state.errorMessage = action.error.message ?? 'That agent could not be deployed.'
      })
      .addCase(saveChat.pending, state => {
        state.strategyStatus = 'saving'
        state.chatMessage = null
      })
      .addCase(saveChat.fulfilled, (state, action: PayloadAction<DashboardChatResponse>) => {
        state.strategyStatus = 'success'
        state.chatMessage = action.payload.summary || null
        state.errorMessage = null
      })
      .addCase(saveChat.rejected, (state, action) => {
        state.strategyStatus = 'error'
        state.chatMessage = null
        state.errorMessage = action.error.message ?? 'That update could not be saved.'
      })
      .addCase(saveKnowledgeItem.rejected, (state, action) => {
        state.errorMessage = action.error.message ?? 'That knowledge item could not be saved.'
      })
      .addCase(uploadKnowledgeFile.rejected, (state, action) => {
        state.errorMessage = action.error.message ?? 'That file could not be uploaded.'
      })
      .addCase(deleteKnowledgeItem.rejected, (state, action) => {
        state.errorMessage = action.error.message ?? 'That knowledge item could not be removed.'
      })
      .addCase(saveInstruction.rejected, (state, action) => {
        state.errorMessage = action.error.message ?? 'That instruction could not be saved.'
      })
      .addCase(deleteInstruction.rejected, (state, action) => {
        state.errorMessage = action.error.message ?? 'That instruction could not be removed.'
      })
  },
})

export const { clearDashboardError, clearSelectedAgent, clearSelectedCall } = dashboardSlice.actions
export const dashboardReducer = dashboardSlice.reducer
